import { ButtonProps, MenuItem } from '@material-ui/core';
import { Save } from '@material-ui/icons';
import React from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { createCenterCostFromApi, updateCenterCostFromApi } from '../../../api/CenterCost/centerCosts';
import TextInputController from '../../../components/Inputs/TextInputController/TextInputController';
import LayoutForm from '../../../components/Layout/LayoutForm';
import ActionsMenu from '../../../components/Layout/Menus/ActionsMenu/ActionsMenu';
import Loading from '../../../components/Loading/Loading';
import SelectController from '../../../components/Select/SelectController';
import Title from '../../../components/Title/Title';
import { tErrorKey, tFormKey, tKey } from '../../../helpers/translate';
import { showSnackbar } from '../../../redux/FeedbackAPI/actions';
import { SeveritySnackbar } from '../../../redux/FeedbackAPI/definitions';
import { getGenericMetadata } from '../../../redux/metadata/actions';
import { AppState } from '../../../redux/root-reducer';
import CenterCostSection from '../../../types/Accounting/CenterCost/CenterCostSection';
import { CenterCostData, CenterCostSectionCode } from '../../../types/Accounting/CenterCost/CenterCostTypes';
import { CostCenterSchema } from '../../../validations/formSchema';
import AccountingSubmenu from '../AccountingSubmenu';
import styles from './costCenter.module.scss';
import useCenterCostForm from './hooks/useCenterCostForm';
import { Role } from '../../../redux/user/definitions';

interface EditCenterCost extends RouteComponentProps<{ id: string; section: CenterCostSectionCode }> {
  getGenericMetadata: () => void;
  showSnackbar: (message: string, severity: SeveritySnackbar, route: string | undefined, time: number) => void;
}

const EditCenterCost: React.FC<EditCenterCost> = ({ match, getGenericMetadata, showSnackbar }) => {
  const {
    params: { id, section },
  } = match;
  const centerCostSection = new CenterCostSection(section);
  const centerCostType = centerCostSection.centerCostTypeOrFail();

  const { centerCost, relatedCenterCosts, loading, roles } = useCenterCostForm(centerCostType, parseInt(id, 10));

  const { handleSubmit, errors, control } = useForm<CenterCostData>({
    mode: 'onSubmit',
    submitFocusError: true,
    validationSchema: CostCenterSchema,
  });

  const title = () => {
    const t_key_section = tKey(centerCostSection.translationKey());
    const section_name = t_key_section.charAt(0).toUpperCase() + t_key_section.slice(1);
    return id === '0'
      ? `${tKey('Creación de centro de coste')}: ${section_name}`
      : `${tKey('Edición de centro de coste')}: ${section_name}`;
  };

  const updateCenterCost = async (data: CenterCostData) => {
    try {
      await updateCenterCostFromApi(centerCostType.value(), data);
      showSnackbar(
        tKey('Formulario actualizado correctamente'),
        'success',
        `/contabilidad/centro-coste/${centerCostSection.value()}`,
        1000,
      );
    } catch (e) {
      showSnackbar(tErrorKey('Error al actualizar el formulario'), 'error', undefined, 1000);
    }
  };

  const createCenterCost = async (data: CenterCostData) => {
    try {
      await createCenterCostFromApi(centerCostType.value(), data);
      getGenericMetadata();
      showSnackbar(tKey('Recurso creado correctamente'), 'success', `/contabilidad/centro-coste/${section}`, 1000);
    } catch (e) {
      showSnackbar(tErrorKey('Error al crear el recurso'), 'error', undefined, 1000);
    }
  };

  const onSave = (data: CenterCostData) => {
    id === '0' ? createCenterCost(data) : updateCenterCost(data);
  };

  const buttonsEdit: ButtonProps[] = [
    {
      children: tKey('Guardar'),
      onClick: handleSubmit(onSave),
      startIcon: <Save />,
      disableElevation: true,
      variant: 'contained',
      color: 'primary',
      fullWidth: true,
    },
  ];

  const renderRelatedCenterCosts = () => {
    const relatedType = centerCostType.relatedType();

    if (!relatedType) {
      return null;
    }

    const center_cost_code = `${relatedType.value()}_code`;

    return (
      <SelectController
        size="50"
        control={control}
        errors={errors}
        schema={CostCenterSchema}
        defaultValue={(centerCost as Record<string, any>)[center_cost_code] || undefined}
        name={center_cost_code}
        label={tKey(relatedType.centerCostSection().translationKey())}
      >
        {relatedCenterCosts.map((type: CenterCostData) => (
          <MenuItem key={type.code} value={type.code}>
            {type.name}
          </MenuItem>
        ))}
      </SelectController>
    );
  };

  const renderRoles = () => {
    if (centerCostType.isArea() || centerCostType.isProject()) {
      return null;
    }

    return (
      <SelectController
        size="50"
        control={control}
        errors={errors}
        schema={CostCenterSchema}
        defaultValue={centerCost.role_code}
        name={'role_code'}
        label={tKey('center_cost.role_code')}
      >
        {roles.map((type: Role) => (
          <MenuItem key={type.code} value={type.code}>
            {type.name}
          </MenuItem>
        ))}
      </SelectController>
    );
  };

  return (
    <LayoutForm
      rightSubmenu={<ActionsMenu actionsButtons={buttonsEdit} />}
      leftSubmenu={<AccountingSubmenu selected={centerCostSection.value()} submenu={tKey('Centro de coste')} />}
    >
      <Title>{title()}</Title>
      {loading ? (
        <Loading big />
      ) : (
        <form onSubmit={handleSubmit(onSave)} className={styles.form_costCenter}>
          <TextInputController
            control={control}
            errors={errors}
            defaultValue={centerCost.id}
            schema={CostCenterSchema}
            name="id"
            type="hidden"
            label={'ID centro de coste'}
            visible={false}
          />
          <TextInputController
            control={control}
            errors={errors}
            defaultValue={centerCost.code}
            schema={CostCenterSchema}
            label={tFormKey('Código')}
            name="code"
            size="50"
          />
          {renderRelatedCenterCosts()}
          {renderRoles()}
          <TextInputController
            control={control}
            errors={errors}
            defaultValue={centerCost.name}
            schema={CostCenterSchema}
            label={tFormKey('Descripción')}
            name="name"
            multiline
            rows="2"
            rowsMax="3"
            fullWidth
          />
        </form>
      )}
    </LayoutForm>
  );
};

const mapStateProps = (state: AppState) => ({});

const mapDispatchToProps = (dispatch: any) => ({
  showSnackbar: (message: string, severity: SeveritySnackbar, route: string | undefined, time: number): void =>
    dispatch(showSnackbar(message, severity, route, time)),
  getGenericMetadata: (): void => dispatch(getGenericMetadata()),
});
export default connect(mapStateProps, mapDispatchToProps)(EditCenterCost);
